// Log-mel spectrogram matching NeMo's AudioToMelSpectrogramPreprocessor.
//
// One MelExtractor per preprocessor config. All scratch buffers and the
// FFT/Hann/Mel filterbank tables are allocated once in the constructor.

import { FFT } from "./fft.js";

const F_SP = 200 / 3;
const MIN_LOG_HZ = 1000;
const MIN_LOG_MEL = MIN_LOG_HZ / F_SP;
const LOG_STEP = Math.log(6.4) / 27;
const LOG_FLOOR = 1e-10;
const VARIANCE_EPSILON = 1e-5;

function hzToMel(hz) {
  return hz < MIN_LOG_HZ ? hz / F_SP : MIN_LOG_MEL + Math.log(hz / MIN_LOG_HZ) / LOG_STEP;
}

function melToHz(mel) {
  return mel < MIN_LOG_MEL ? mel * F_SP : MIN_LOG_HZ * Math.exp(LOG_STEP * (mel - MIN_LOG_MEL));
}

function buildHannWindow(winLen) {
  const w = new Float32Array(winLen);
  for (let i = 0; i < winLen; i += 1) {
    w[i] = 0.5 * (1 - Math.cos((2 * Math.PI * i) / winLen));
  }
  return w;
}

function buildMelFilterbank(sr, nFft, nMels) {
  const nBins = (nFft >> 1) + 1;
  const fftFreqs = new Float64Array(nBins);
  for (let k = 0; k < nBins; k += 1) fftFreqs[k] = (k * sr) / nFft;

  const melMin = hzToMel(0);
  const melMax = hzToMel(sr / 2);
  const melHz = new Float64Array(nMels + 2);
  for (let i = 0; i < nMels + 2; i += 1) {
    melHz[i] = melToHz(melMin + (i * (melMax - melMin)) / (nMels + 1));
  }

  const filters = new Float32Array(nMels * nBins);
  const starts = new Int32Array(nMels);
  const ends = new Int32Array(nMels);
  for (let m = 0; m < nMels; m += 1) {
    const tri = { lo: melHz[m], ctr: melHz[m + 1], hi: melHz[m + 2] };
    fillTriangle(filters, m, nBins, tri, fftFreqs);
    [starts[m], ends[m]] = trimEdges(filters, m, nBins);
  }
  return { filters, starts, ends, nBins };
}

function fillTriangle(filters, m, nBins, tri, fftFreqs) {
  const { lo, ctr, hi } = tri;
  const base = m * nBins;
  const dLo = ctr - lo || 1;
  const dHi = hi - ctr || 1;
  const norm = 2 / (hi - lo || 1);
  for (let k = 0; k < nBins; k += 1) {
    const f = fftFreqs[k];
    let v = 0;
    if (f >= lo && f <= ctr) v = (f - lo) / dLo;
    else if (f > ctr && f <= hi) v = (hi - f) / dHi;
    filters[base + k] = v * norm;
  }
}

function trimEdges(filters, m, nBins) {
  const base = m * nBins;
  let s = 0;
  while (s < nBins && filters[base + s] === 0) s += 1;
  let e = nBins;
  while (e > s && filters[base + e - 1] === 0) e -= 1;
  return [s, e];
}

export class MelExtractor {
  constructor(cfg, capacity) {
    this.sr = cfg.sample_rate;
    this.nFft = cfg.n_fft;
    this.hop = cfg.hop_length;
    this.winLen = cfg.win_length;
    this.nMels = cfg.features;
    this.normalize = cfg.normalize === "per_feature";

    this.fft = new FFT(this.nFft);
    this.hann = buildHannWindow(this.winLen);
    const bank = buildMelFilterbank(this.sr, this.nFft, this.nMels);
    this.filters = bank.filters;
    this.melStarts = bank.starts;
    this.melEnds = bank.ends;
    this.nBins = bank.nBins;

    this.fftReal = new Float32Array(this.nFft);
    this.fftImag = new Float32Array(this.nFft);
    this.power = new Float32Array(this.nBins);

    // Persistent reflect-pad scratch sized for the worst-case window.
    // The caller (engine.js) must specify a capacity — there is no sensible
    // default since "max samples per call" is a property of the deployment,
    // not of the mel extractor.
    if (!Number.isFinite(capacity) || capacity <= 0) {
      throw new Error("MelExtractor: capacity (max samples per call) is required");
    }
    this.maxAudio = capacity;
    this.padded = new Float32Array(this.maxAudio + this.nFft);
  }

  framesFor(audioLen) {
    const padded = audioLen + 2 * (this.nFft >> 1);
    return 1 + Math.floor((padded - this.winLen) / this.hop);
  }

  extract(audio, out) {
    if (audio.length > this.maxAudio) {
      throw new Error(`mel.extract: audio length ${audio.length} exceeds capacity ${this.maxAudio}`);
    }
    const nFrames = this.framesFor(audio.length);
    const expected = this.nMels * nFrames;
    if (!out || out.length < expected) {
      throw new Error(`mel.extract: out buffer too small (need ${expected}, got ${out?.length ?? 0})`);
    }
    this._fillFrames(audio, out, nFrames);
    if (this.normalize) normalizePerFeature(out, this.nMels, nFrames);
    return { data: out, nMels: this.nMels, nFrames };
  }

  _fillFrames(audio, logMel, nFrames) {
    reflectPadInto(audio, this.nFft >> 1, this.padded);
    for (let f = 0; f < nFrames; f += 1) {
      this._windowAndFft(this.padded, f * this.hop);
      this._toMelFrame(logMel, f, nFrames);
    }
  }

  _windowAndFft(padded, offset) {
    const { fftReal, fftImag, hann, winLen, nFft } = this;
    for (let i = 0; i < winLen; i += 1) fftReal[i] = padded[offset + i] * hann[i];
    for (let i = winLen; i < nFft; i += 1) fftReal[i] = 0;
    fftImag.fill(0);
    this.fft.forward(fftReal, fftImag);
    for (let k = 0; k < this.nBins; k += 1) {
      this.power[k] = fftReal[k] * fftReal[k] + fftImag[k] * fftImag[k];
    }
  }

  _toMelFrame(logMel, f, nFrames) {
    const { filters, melStarts, melEnds, nMels, nBins, power } = this;
    for (let m = 0; m < nMels; m += 1) {
      const rowBase = m * nBins;
      const ks = melStarts[m];
      const ke = melEnds[m];
      let sum = 0;
      for (let k = ks; k < ke; k += 1) sum += filters[rowBase + k] * power[k];
      logMel[m * nFrames + f] = Math.log(sum > LOG_FLOOR ? sum : LOG_FLOOR);
    }
  }
}

function reflectPadInto(audio, pad, scratch) {
  const len = audio.length;
  const total = pad + len + pad;
  // PyTorch `F.pad(mode='reflect')` semantics — boundary element is NOT
  // repeated (that would be `mode='symmetric'`, the previous bug here).
  // Example for [a,b,c,d] with pad=2: result is [c,b,a,b,c,d,c,b].
  // Left source indices are [1, 2, ..., pad]; right are [len-2, ..., len-1-pad].
  // NeMo's AudioToMelSpectrogramPreprocessor uses torchaudio's reflect, so
  // training-time and inference-time framing must match here.
  for (let i = 0; i < pad; i += 1) {
    const src = i + 1;
    scratch[pad - 1 - i] = src < len ? audio[src] : 0;
  }
  scratch.set(audio, pad);
  for (let i = 0; i < pad; i += 1) {
    const src = len - 2 - i;
    scratch[pad + len + i] = src >= 0 ? audio[src] : 0;
  }
  // Zero the tail beyond the active region so any subsequent FFT can't read stale data.
  for (let i = total; i < scratch.length; i += 1) scratch[i] = 0;
}

function normalizePerFeature(logMel, nMels, nFrames) {
  for (let m = 0; m < nMels; m += 1) {
    const base = m * nFrames;
    let mean = 0;
    for (let f = 0; f < nFrames; f += 1) mean += logMel[base + f];
    mean /= nFrames;
    let variance = 0;
    for (let f = 0; f < nFrames; f += 1) {
      const d = logMel[base + f] - mean;
      variance += d * d;
    }
    const std = Math.sqrt(variance / nFrames + VARIANCE_EPSILON);
    for (let f = 0; f < nFrames; f += 1) logMel[base + f] = (logMel[base + f] - mean) / std;
  }
}
